// src/context/AuthContext.js
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";
import {
  getSafeOrgLoadDiagnostic,
  listOrgs,
  listOrgsByIds,
} from "../utils/orgData";
import { syncSessionsFromFirestore } from "../utils/sessionSync";
import { logOrgActivity, logOrgSwitch } from "../utils/orgAudit";
import {
  getCanonicalOrgMembershipForAccount,
  syncOrgUserForAccount,
} from "../utils/orgUsers";
import { normalizeProfileLocation } from "../utils/locationOptions";
import { getProfile, saveProfile } from "../utils/roles";
import { registerRuntimeTestAction } from "../utils/runtimeDiagnostics";

const AuthContext = createContext();
const PROFILE_BOOTSTRAP_TIMEOUT_MS = 8000;
const AUTH_LOADING_TIMEOUT_MS = 12000;
const DEFAULT_ORG_ID = "org_default";
export const ORG_LOAD_STATUS = Object.freeze({
  LOADING: "loading",
  POPULATED: "populated",
  EMPTY: "empty",
  ERROR: "error",
  NOT_REQUIRED: "not-required",
});

const buildUsername = (user) => {
  if (!user) return "member";
  if (user.displayName) return user.displayName;
  if (user.email) return user.email.split("@")[0];
  return "member";
};

export const isFallbackOrgId = (orgId) => {
  const value = String(orgId || "").trim();
  return !value || value === DEFAULT_ORG_ID || value.startsWith("demo_");
};

export const normalizeOrgIds = (orgIds = []) =>
  Array.from(new Set(orgIds.map((orgId) => String(orgId || "").trim()).filter(Boolean)));

export const getRealOrgIds = (orgIds = []) =>
  normalizeOrgIds(orgIds).filter((orgId) => !isFallbackOrgId(orgId));

export const getSingleRealOrgId = (orgIds = []) => {
  const realOrgIds = getRealOrgIds(orgIds);
  return realOrgIds.length === 1 ? realOrgIds[0] : null;
};

export function resolveForcedSingleOrgId(user = null) {
  if (!user || String(user.role || "").toLowerCase() === "superadmin") return null;
  return getSingleRealOrgId([
    user.orgId,
    ...(Array.isArray(user.orgIds) ? user.orgIds : []),
  ]);
}

const CANONICAL_NON_SUPERADMIN_ROLES = new Set([
  "admin",
  "coach",
  "member",
  "investor",
]);

export function normalizeAccountRole(value, fallback = "member") {
  const role = String(value || "").trim().toLowerCase();
  if (role === "superadmin" || CANONICAL_NON_SUPERADMIN_ROLES.has(role)) {
    return role;
  }
  const safeFallback = String(fallback || "member").trim().toLowerCase();
  return safeFallback === "superadmin" ||
    CANONICAL_NON_SUPERADMIN_ROLES.has(safeFallback)
    ? safeFallback
    : "member";
}

export function resolveAuthoritativeRole({
  profileRole = "member",
  membershipRole = "",
} = {}) {
  const safeProfileRole = normalizeAccountRole(profileRole);
  if (safeProfileRole === "superadmin") return "superadmin";
  const safeMembershipRole = String(membershipRole || "").trim().toLowerCase();
  return CANONICAL_NON_SUPERADMIN_ROLES.has(safeMembershipRole)
    ? safeMembershipRole
    : safeProfileRole;
}

export function getActiveCanonicalMembershipRole(membership = null) {
  if (!membership || typeof membership !== "object" || Array.isArray(membership)) {
    return "";
  }
  const status = String(membership.status || "").trim().toLowerCase();
  const role = String(membership.role || "").trim().toLowerCase();
  if (
    membership.disabled === true ||
    (status && status !== "active") ||
    !CANONICAL_NON_SUPERADMIN_ROLES.has(role)
  ) {
    return "";
  }
  return role;
}

export function canRoleSwitchOrganizations(user = null) {
  const role = String(user?.role || "").trim().toLowerCase();
  return role === "superadmin" || CANONICAL_NON_SUPERADMIN_ROLES.has(role);
}

export function getMultiOrgAuthorizationCandidates(user = null) {
  const role = String(user?.role || "").trim().toLowerCase();
  if (!user?.uid || !CANONICAL_NON_SUPERADMIN_ROLES.has(role)) {
    return [];
  }
  return getRealOrgIds([
    user.orgId,
    ...(Array.isArray(user.orgIds) ? user.orgIds : []),
  ]);
}

export function requiresAuthorizedOrgList(user = null) {
  return Boolean(user?.uid);
}

export function shouldShowOrganizationSelector({
  user = null,
  orgs = [],
  orgLoadStatus = "",
} = {}) {
  if (String(user?.role || "").trim().toLowerCase() === "superadmin") return true;
  if (!canRoleSwitchOrganizations(user)) return false;
  return orgLoadStatus === ORG_LOAD_STATUS.POPULATED && orgs.length > 1;
}

export function isAuthorizedOrgSelection({
  user = null,
  orgs = [],
  orgId = "",
  orgsLoading = false,
  orgLoadStatus = "",
} = {}) {
  const safeOrgId = String(orgId || "").trim();
  if (!user?.uid || !safeOrgId) return false;
  if (requiresAuthorizedOrgList(user)) {
    if (
      orgsLoading ||
      (orgLoadStatus && orgLoadStatus !== ORG_LOAD_STATUS.POPULATED)
    ) {
      return false;
    }
    return normalizeOrgIds(orgs.map((org) => org?.id)).includes(safeOrgId);
  }
  const pinnedOrgId =
    resolveForcedSingleOrgId(user) ||
    String(user.orgId || "").trim() ||
    DEFAULT_ORG_ID;
  return safeOrgId === pinnedOrgId;
}

export function resolveDashboardQueryOrgId(
  user = null,
  activeOrgId = "",
  orgAccessReady = true
) {
  if (!orgAccessReady) return "";
  const safeActiveOrgId = String(activeOrgId || "").trim();
  return safeActiveOrgId;
}

export function getOrgSelectorPresentation(orgLoadStatus, orgs = []) {
  if (orgLoadStatus === ORG_LOAD_STATUS.LOADING) {
    return {
      disabled: true,
      optionLabel: "Loading organisations…",
      statusMessage: "Organisation access is loading.",
    };
  }
  if (orgLoadStatus === ORG_LOAD_STATUS.ERROR) {
    return {
      disabled: true,
      optionLabel: "Organisations unavailable",
      statusMessage: "Organisation access could not be loaded. The existing selection was not changed.",
    };
  }
  if (orgLoadStatus === ORG_LOAD_STATUS.EMPTY) {
    return {
      disabled: false,
      optionLabel: "No authorised organisations",
      statusMessage: "No authorised organisations are currently available.",
    };
  }
  return {
    disabled: false,
    optionLabel: "",
    statusMessage: "",
  };
}

export function readStoredActiveOrgId(storage) {
  try {
    const source = storage || (
      typeof window !== "undefined" ? window.localStorage : null
    );
    return String(source?.getItem("nia_active_org") || "").trim() || null;
  } catch {
    return null;
  }
}

export function preserveGlobalFirestoreErrorAfterOrgLoadFailure(currentError) {
  return currentError;
}

export function resolveOrgMembership(profile = {}, existingUser = null) {
  const explicitOrgId = String(profile.orgId || "").trim();
  const listedOrgIds = Array.isArray(profile.orgIds) ? normalizeOrgIds(profile.orgIds) : [];
  const existingOrgId = String(existingUser?.orgId || "").trim();
  const existingOrgIds = Array.isArray(existingUser?.orgIds) ? normalizeOrgIds(existingUser.orgIds) : [];
  const profileOrgIds = normalizeOrgIds([explicitOrgId, ...listedOrgIds]);
  const realProfileOrgIds = getRealOrgIds(profileOrgIds);
  const combinedOrgIds = realProfileOrgIds.length
    ? profileOrgIds
    : normalizeOrgIds([existingOrgId, explicitOrgId, ...existingOrgIds, ...listedOrgIds]);
  const realOrgIds = getRealOrgIds(combinedOrgIds);
  const resolvedOrgId =
    (realOrgIds.length === 1 && realOrgIds[0]) ||
    (!isFallbackOrgId(existingOrgId) && existingOrgId) ||
    (!isFallbackOrgId(explicitOrgId) && explicitOrgId) ||
    realOrgIds[0] ||
    existingOrgId ||
    explicitOrgId ||
    combinedOrgIds[0] ||
    DEFAULT_ORG_ID;

  return {
    orgId: resolvedOrgId,
    orgIds: combinedOrgIds.length ? combinedOrgIds : [DEFAULT_ORG_ID],
  };
}

export function resolvePreferredActiveOrgId({
  explicitSelectedOrgId,
  preferredOrgId,
  membershipOrgIds = [],
  visibleOrgIds = [],
  storedOrgId,
}) {
  const allowed = normalizeOrgIds(
    (visibleOrgIds.length ? visibleOrgIds : membershipOrgIds)
  );
  const realAllowed = getRealOrgIds(allowed);

  if (realAllowed.length === 1) return realAllowed[0];
  const preferredAllowed = Array.from(
    new Set(
      [...allowed, preferredOrgId, storedOrgId, explicitSelectedOrgId]
        .map((orgId) => String(orgId || "").trim())
        .filter(Boolean)
    )
  );

  if (explicitSelectedOrgId && preferredAllowed.includes(explicitSelectedOrgId) && allowed.includes(explicitSelectedOrgId)) return explicitSelectedOrgId;
  if (preferredOrgId && realAllowed.includes(preferredOrgId)) return preferredOrgId;
  if (storedOrgId && !isFallbackOrgId(storedOrgId) && allowed.includes(storedOrgId)) return storedOrgId;
  if (realAllowed.length) return realAllowed[0];
  if (explicitSelectedOrgId && realAllowed.includes(explicitSelectedOrgId)) return explicitSelectedOrgId;
  if (preferredOrgId && allowed.includes(preferredOrgId)) return preferredOrgId;
  if (storedOrgId && allowed.includes(storedOrgId)) return storedOrgId;
  return allowed[0] || explicitSelectedOrgId || preferredOrgId || storedOrgId || DEFAULT_ORG_ID;
}

function withTimeout(promise, ms = PROFILE_BOOTSTRAP_TIMEOUT_MS) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

export function buildResolvedUser(
  firebaseUser,
  profile = {},
  existingUser = null,
  { authorityTrusted = true } = {}
) {
  const authorityProfile = authorityTrusted ? profile : {};
  const authorityExistingUser = authorityTrusted ? existingUser : null;
  const resolvedMembership = resolveOrgMembership(
    authorityProfile,
    authorityExistingUser
  );
  const resolvedPhotoURL = String(profile.photoURL || firebaseUser.photoURL || "").trim();
  return {
    uid: firebaseUser.uid,
    email: profile.email || firebaseUser.email || "",
    username: profile.username || buildUsername(firebaseUser),
    firstName: profile.firstName || "",
    lastName: profile.lastName || "",
    country: profile.country || profile.countryCode || "",
    province: profile.province || profile.state || profile.region || "",
    city: profile.city || profile.town || profile.locality || "",
    photoURL: resolvedPhotoURL,
    dashboardActionState:
      profile.dashboardActionState && typeof profile.dashboardActionState === "object"
        ? profile.dashboardActionState
        : {},
    role: authorityTrusted
      ? normalizeAccountRole(profile.role || existingUser?.role)
      : "member",
    orgId: resolvedMembership.orgId,
    orgIds: resolvedMembership.orgIds,
  };
}

function cacheResolvedProfile(firebaseUser, profile = {}) {
  const resolvedMembership = resolveOrgMembership(profile);
  try {
    saveProfile({
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      username: profile.username || buildUsername(firebaseUser),
      email: profile.email || firebaseUser.email || "",
      country: profile.country || profile.countryCode || "",
      province: profile.province || profile.state || profile.region || "",
      city: profile.city || profile.town || profile.locality || "",
      photoURL: String(profile.photoURL || firebaseUser.photoURL || "").trim(),
      role: profile.role || "member",
      orgId: resolvedMembership.orgId,
      orgIds: resolvedMembership.orgIds,
    });
  } catch {}
}

async function ensureUserProfile(firebaseUser) {
  const ref = doc(db, "users", firebaseUser.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    const profile = {
      uid: firebaseUser.uid,
      email: firebaseUser.email || "",
      username: buildUsername(firebaseUser),
      photoURL: firebaseUser.photoURL || "",
      dashboardActionState: {},
      role: "member",
      orgId: DEFAULT_ORG_ID,
      orgIds: [DEFAULT_ORG_ID],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(ref, profile);
    return profile;
  }
  const data = snap.data() || {};
  if (!Array.isArray(data.orgIds) || !data.orgIds.length) {
    return {
      ...data,
      photoURL: data.photoURL || data.photoDataUrl || firebaseUser.photoURL || "",
      dashboardActionState:
        data.dashboardActionState && typeof data.dashboardActionState === "object"
          ? data.dashboardActionState
          : {},
      orgIds: [data.orgId || DEFAULT_ORG_ID],
    };
  }
  return {
    ...data,
    photoURL: data.photoURL || data.photoDataUrl || firebaseUser.photoURL || "",
    dashboardActionState:
      data.dashboardActionState && typeof data.dashboardActionState === "object"
        ? data.dashboardActionState
        : {},
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgLoadStatus, setOrgLoadStatus] = useState(ORG_LOAD_STATUS.LOADING);
  const [orgLoadError, setOrgLoadError] = useState("");
  const [orgSelectionError, setOrgSelectionError] = useState("");
  const [roleAuthorityOrgId, setRoleAuthorityOrgId] = useState("");
  const [profileAuthorityReady, setProfileAuthorityReady] = useState(false);
  const [firestoreError, setFirestoreError] = useState("");
  const [viewRole, setViewRole] = useState(() => {
    try {
      return localStorage.getItem("nia_view_role") || "";
    } catch {
      return "";
    }
  });
  const [activeOrgId, setActiveOrgIdState] = useState(() => (
    readStoredActiveOrgId()
  ));
  const authStateSeqRef = useRef(0);
  const explicitActiveOrgRef = useRef(activeOrgId || null);

  const setActiveOrgId = useCallback((nextOrgId) => {
    const normalized = String(
      typeof nextOrgId === "function" ? nextOrgId(explicitActiveOrgRef.current || activeOrgId || null) : (nextOrgId || "")
    ).trim() || null;
    if (!isAuthorizedOrgSelection({
      user,
      orgs,
      orgId: normalized,
      orgsLoading,
      orgLoadStatus,
    })) {
      setOrgSelectionError(
        orgsLoading
          ? "Organisation access is still loading."
          : orgLoadStatus === ORG_LOAD_STATUS.ERROR
            ? "Organisation access could not be loaded."
          : "That organisation is not available to this account."
      );
      return false;
    }
    explicitActiveOrgRef.current = normalized;
    setOrgSelectionError("");
    try {
      if (normalized) localStorage.setItem("nia_active_org", normalized);
      else localStorage.removeItem("nia_active_org");
    } catch {}
    setActiveOrgIdState(normalized);
    return true;
  }, [activeOrgId, orgLoadStatus, orgs, orgsLoading, user]);
  const forcedSingleOrgId = resolveForcedSingleOrgId(user);
  const orgAuthorizationRequired = requiresAuthorizedOrgList(user);
  const resolvedActiveOrgId = !user?.uid
    ? null
    : orgAuthorizationRequired
    ? (
      isAuthorizedOrgSelection({
        user,
        orgs,
        orgId: activeOrgId,
        orgsLoading,
        orgLoadStatus,
      })
        ? activeOrgId
        : null
      )
    : (forcedSingleOrgId || user?.orgId || DEFAULT_ORG_ID);
  const roleAuthorityReady =
    user?.role === "superadmin" ||
    Boolean(resolvedActiveOrgId && roleAuthorityOrgId === resolvedActiveOrgId);
  const orgAccessReady = Boolean(resolvedActiveOrgId) && roleAuthorityReady;

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          const profile = await withTimeout(ensureUserProfile(result.user));
          cacheResolvedProfile(result.user, profile);
        }
      } catch {
        // Redirect auth failures are surfaced by loginWithGoogle caller.
      }
    };
    handleRedirect();
  }, []);

  useEffect(() => {
    let active = true;
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      const seq = ++authStateSeqRef.current;
      const isCurrent = () => active && seq === authStateSeqRef.current;

      if (!firebaseUser) {
        setOrgs([]);
        setOrgsLoading(false);
        setOrgSelectionError("");
        setFirestoreError("");
        setRoleAuthorityOrgId("");
        setProfileAuthorityReady(false);
        setUser(null);
        explicitActiveOrgRef.current = null;
        setLoading(false);
        return;
      }
      const cachedProfile = getProfile();
      setProfileAuthorityReady(false);
      setRoleAuthorityOrgId("");
      setUser((prev) => buildResolvedUser(
        firebaseUser,
        cachedProfile,
        prev,
        { authorityTrusted: false }
      ));
      setLoading(false);

      (async () => {
        try {
          const profile = await withTimeout(ensureUserProfile(firebaseUser));
          if (!isCurrent()) return;
          cacheResolvedProfile(firebaseUser, profile);
          setFirestoreError("");
          setProfileAuthorityReady(true);

          const loginStamp = firebaseUser?.metadata?.lastSignInTime || new Date().toISOString();
          const creationStamp = firebaseUser?.metadata?.creationTime || "";
          const isFirstLogin = Boolean(
            creationStamp &&
            loginStamp &&
            Date.parse(String(creationStamp)) === Date.parse(String(loginStamp))
          );
          const membership = resolveOrgMembership(profile);
          const membershipOrgIds = membership.orgIds;
          const syncUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || "",
            username: profile.username || buildUsername(firebaseUser),
            role: profile.role || "member",
          };

          Promise.all(
            membershipOrgIds.map((orgId) =>
              syncOrgUserForAccount(syncUser, orgId, {
                lastSignInTime: loginStamp,
                lastLogin: loginStamp,
              })
            )
          ).catch(() => {});

          if (isFirstLogin && !profile?.firstLoginWelcomeNotifiedAt) {
            Promise.all(
              membershipOrgIds.map((orgId) =>
                logOrgActivity(
                  {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email || "",
                    role: profile.role || "member",
                  },
                  orgId,
                  "first_login_welcome",
                  {
                    targetUserId: firebaseUser.uid,
                    targetEmail: firebaseUser.email || "",
                    message: "Welcome to Nia COACH. Start by opening a chat and saying: Hello Nia, this is my first session.",
                  }
                )
              )
            ).catch(() => {});
            updateDoc(doc(db, "users", firebaseUser.uid), {
              firstLoginWelcomeNotifiedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }).catch(() => {});
          }

          logOrgActivity(
            {
              uid: firebaseUser.uid,
              email: firebaseUser.email || "",
              role: profile.role || "member",
            },
            membership.orgId || DEFAULT_ORG_ID,
            "login",
            { userEmail: firebaseUser.email || "" }
          );

          setUser((prev) => buildResolvedUser(firebaseUser, profile, prev));
        } catch (error) {
          if (!isCurrent()) return;
          const isTimeout = String(error?.message || "") === "timeout";
          if (isTimeout) {
            setFirestoreError("Account details are taking longer than expected. Loaded a basic session and will keep refreshing in the background.");
          }
          setUser((prev) => prev || buildResolvedUser(
            firebaseUser,
            cachedProfile,
            prev,
            { authorityTrusted: false }
          ));
        }
      })();
    });

    return () => {
      active = false;
      unsub();
    };
  }, []);

  useEffect(() => {
    if (!forcedSingleOrgId) return;
    explicitActiveOrgRef.current = forcedSingleOrgId;
    setActiveOrgIdState((prev) => (prev === forcedSingleOrgId ? prev : forcedSingleOrgId));
    try {
      localStorage.setItem("nia_active_org", forcedSingleOrgId);
    } catch {}
  }, [forcedSingleOrgId]);

  useEffect(() => {
    if (!loading) return undefined;
    const id = setTimeout(() => {
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        const cachedProfile = getProfile();
        setUser((prev) => prev || buildResolvedUser(
          firebaseUser,
          cachedProfile,
          prev,
          { authorityTrusted: false }
        ));
        setFirestoreError((prev) => (
          prev || "Account loading took too long. Loaded a recoverable session; account details may still refresh."
        ));
      }
      setLoading(false);
    }, AUTH_LOADING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [loading]);

  useEffect(() => {
    if (!user?.uid) return;
    const orgId = resolveDashboardQueryOrgId(
      user,
      resolvedActiveOrgId,
      orgAccessReady
    );
    if (!orgId) return;
    const onError = (err) => {
      const detail = err?.code || err?.message || "";
      const suffix = detail ? ` (${detail})` : "";
      setFirestoreError(`Firestore is temporarily unavailable${suffix}. Some data may be stale.`);
      // Surface to console for debugging permission/availability issues.
      try {
        // eslint-disable-next-line no-console
        console.warn("Firestore sync error:", err);
      } catch {}
    };
    syncSessionsFromFirestore(user, orgId, onError);
  }, [user, resolvedActiveOrgId, orgAccessReady]);

  useEffect(() => {
    if (!user?.uid) return;
    if (!profileAuthorityReady) {
      setOrgs([]);
      setOrgsLoading(true);
      setOrgLoadStatus(ORG_LOAD_STATUS.LOADING);
      return;
    }
    let mounted = true;
    const loadOrgs = async () => {
      setOrgsLoading(true);
      setOrgLoadStatus(ORG_LOAD_STATUS.LOADING);
      setOrgLoadError("");
      setOrgSelectionError("");
      try {
        let visibleOrgs = [];
        const isGlobalSuperadmin = user.role === "superadmin";
        const authorizationCandidates = getMultiOrgAuthorizationCandidates(user);
        if (isGlobalSuperadmin) {
          const list = await listOrgs(user, { throwOnError: true });
          if (!mounted) return;
          if (!list.length) {
            setOrgs([]);
            setOrgLoadStatus(ORG_LOAD_STATUS.EMPTY);
            return;
          }
          visibleOrgs = list;
          setOrgs(visibleOrgs);
          setOrgLoadStatus(ORG_LOAD_STATUS.POPULATED);
        } else if (authorizationCandidates.length) {
          const list = await listOrgsByIds(user, authorizationCandidates);
          if (!mounted) return;
          visibleOrgs = list;
          setOrgs(list);
          setOrgLoadStatus(
            list.length ? ORG_LOAD_STATUS.POPULATED : ORG_LOAD_STATUS.EMPTY
          );
        } else {
          setOrgs([]);
          setOrgLoadStatus(ORG_LOAD_STATUS.EMPTY);
        }
        if (!mounted) return;
        const preferred = user.orgId || DEFAULT_ORG_ID;
        const visibleIds = visibleOrgs.map((org) => org.id).filter(Boolean);
        setActiveOrgIdState((prev) => {
          if (isGlobalSuperadmin) {
            const existingSelection = String(
              explicitActiveOrgRef.current || prev || ""
            ).trim();
            if (existingSelection) {
              if (visibleIds.includes(existingSelection)) {
                explicitActiveOrgRef.current = existingSelection;
                return existingSelection;
              }
              setOrgSelectionError(
                "The stored organisation is not available to this account. Choose an authorised organisation."
              );
              return null;
            }
          }
          if (!isGlobalSuperadmin && !visibleIds.length) {
            explicitActiveOrgRef.current = null;
            return null;
          }
          const membershipIds =
            authorizationCandidates.length ? authorizationCandidates : [preferred];
          const resolved = resolvePreferredActiveOrgId({
            explicitSelectedOrgId: explicitActiveOrgRef.current,
            preferredOrgId: preferred,
            membershipOrgIds: membershipIds,
            visibleOrgIds: visibleIds,
            storedOrgId: prev,
          });
          if (!visibleIds.length && !membershipIds.includes(explicitActiveOrgRef.current)) {
            explicitActiveOrgRef.current = resolved;
          } else if (visibleIds.length && !visibleIds.includes(explicitActiveOrgRef.current)) {
            explicitActiveOrgRef.current = resolved;
          }
          return resolved;
        });
      } catch (error) {
        if (!mounted) return;
        setOrgs([]);
        setOrgLoadStatus(ORG_LOAD_STATUS.ERROR);
        setOrgLoadError(
          "Organisation access could not be loaded. The existing selection was not changed."
        );
        setFirestoreError(preserveGlobalFirestoreErrorAfterOrgLoadFailure);
        try {
          // eslint-disable-next-line no-console
          console.warn(
            "Authorised organisation load failed.",
            getSafeOrgLoadDiagnostic(error)
          );
        } catch {}
      } finally {
        if (mounted) setOrgsLoading(false);
      }
    };
    loadOrgs();
    return () => {
      mounted = false;
    };
  }, [profileAuthorityReady, user]);

  useEffect(() => {
    if (!resolvedActiveOrgId) return;
    try {
      localStorage.setItem("nia_active_org", resolvedActiveOrgId);
    } catch {}
    if (user?.role === "superadmin" || user?.role === "coach" || user?.role === "admin") {
      logOrgSwitch(user, resolvedActiveOrgId);
    }
  }, [resolvedActiveOrgId, user]);

  useEffect(() => {
    const accountUid = user?.uid;
    if (!accountUid) return;
    if (user?.role === "superadmin") return;
    const orgId = resolvedActiveOrgId;
    if (!orgId) return;
    let mounted = true;
    getCanonicalOrgMembershipForAccount({ uid: accountUid }, orgId)
      .then((orgUser) => {
        if (!mounted) return;
        if (!orgUser) {
          setOrgSelectionError(
            "The selected organisation membership is not available to this account."
          );
          return;
        }
        const membershipRole = getActiveCanonicalMembershipRole(orgUser);
        if (!membershipRole) {
          setOrgSelectionError(
            "The selected organisation membership is not active and valid."
          );
          return;
        }
        const nextFirstName = String(orgUser.firstName || "").trim();
        const nextLastName = String(orgUser.surname || orgUser.lastName || "").trim();
        const nextUsername = String(orgUser.username || "").trim();
        const nextEmail = String(orgUser.email || "").trim();
        setUser((prev) => {
          if (!prev) return prev;
          const nextRole = resolveAuthoritativeRole({
            profileRole: prev.role,
            membershipRole,
          });
          const merged = {
            ...prev,
            firstName: nextFirstName || prev.firstName || "",
            lastName: nextLastName || prev.lastName || "",
            username: nextUsername || prev.username,
            email: nextEmail || prev.email,
            role: nextRole,
          };
          if (
            merged.firstName === prev.firstName &&
            merged.lastName === prev.lastName &&
            merged.username === prev.username &&
            merged.email === prev.email &&
            merged.role === prev.role
          ) {
            return prev;
          }
          return merged;
        });
        setRoleAuthorityOrgId(orgId);
        try {
          const current = getProfile();
          saveProfile({
            ...current,
            firstName: nextFirstName || current.firstName || "",
            lastName: nextLastName || current.lastName || "",
            username: nextUsername || current.username || "",
            email: nextEmail || current.email || "",
          });
        } catch {}
      })
      .catch(() => {
        if (!mounted) return;
        setOrgSelectionError(
          "Organisation membership could not be securely refreshed."
        );
      });
    return () => {
      mounted = false;
    };
  }, [user?.uid, user?.role, resolvedActiveOrgId]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const signup = async (email, password) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await ensureUserProfile(cred.user);
    return cred;
  };

  const loginWithGoogle = async () => {
    const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/i.test(navigator.userAgent || "");
    try {
      setLoading(true);
      if (isIOS) {
        await signInWithRedirect(auth, googleProvider);
        return { redirect: true };
      }
      const cred = await signInWithPopup(auth, googleProvider);
      await ensureUserProfile(cred.user);
      return cred;
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const logout = useCallback(() => signOut(auth), []);

  useEffect(
    () => registerRuntimeTestAction("signOut", logout),
    [logout]
  );

  const updateRole = useCallback(async (role) => {
    if (!user?.uid || user?.role !== "superadmin") return;
    const ref = doc(db, "users", user.uid);
    await updateDoc(ref, { role, updatedAt: serverTimestamp() });
    setUser((prev) => (prev ? { ...prev, role } : prev));
  }, [user?.role, user?.uid]);

  const updateProfile = useCallback(
    async (updates = {}) => {
      if (!user?.uid) return false;
      const currentOrgId = resolvedActiveOrgId;
      const currentOrgIds = Array.from(
        new Set(
          [currentOrgId, ...(Array.isArray(user.orgIds) ? user.orgIds : [])]
            .map((orgId) => String(orgId || "").trim())
            .filter(Boolean)
        )
      );
      const normalizedLocation = normalizeProfileLocation({
        country: updates.country,
        province: updates.province,
        city: updates.city,
      });
      const next = {
        firstName: String(updates.firstName || "").trim(),
        lastName: String(updates.lastName || "").trim(),
        username: String(updates.username || "").trim(),
        email: String(updates.email || "").trim(),
        country: normalizedLocation.country,
        province: normalizedLocation.province,
        city: normalizedLocation.city,
        photoURL: String(updates.photoURL || "").trim(),
        orgId: currentOrgId,
        orgIds: currentOrgIds,
      };
      const payload = {
        firstName: next.firstName,
        lastName: next.lastName,
        username: next.username,
        email: next.email,
        country: next.country,
        province: next.province,
        city: next.city,
        photoURL: next.photoURL,
        orgId: next.orgId,
        orgIds: next.orgIds,
        updatedAt: serverTimestamp(),
      };
      try {
        await setDoc(doc(db, "users", user.uid), payload, { merge: true });
      } catch {
        return false;
      }
      syncOrgUserForAccount(user, currentOrgId, {
        firstName: next.firstName,
        surname: next.lastName,
        username: next.username,
        email: next.email,
        photoURL: next.photoURL,
      }).catch(() => {});
      setUser((prev) => (
        prev
          ? {
              ...prev,
              ...next,
              photoURL: next.photoURL || prev.photoURL || "",
            }
          : prev
      ));
      try {
        const current = getProfile();
        saveProfile({
          ...current,
          firstName: next.firstName,
          lastName: next.lastName,
          username: next.username,
          email: next.email,
          country: next.country,
          province: next.province,
          city: next.city,
          photoURL: next.photoURL,
          orgId: next.orgId,
          orgIds: next.orgIds,
        });
      } catch {}
      return true;
    },
    [user, resolvedActiveOrgId]
  );

  const updateDashboardActionState = useCallback(
    async (updates = {}) => {
      if (!user?.uid) return false;
      const current =
        user?.dashboardActionState && typeof user.dashboardActionState === "object"
          ? user.dashboardActionState
          : {};
      const next = { ...current, ...updates };
      try {
        await setDoc(
          doc(db, "users", user.uid),
          {
            dashboardActionState: next,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch {
        return false;
      }
      setUser((prev) => (prev ? { ...prev, dashboardActionState: next } : prev));
      return true;
    },
    [user]
  );

  useEffect(() => {
    if (!user?.role) return;
    if (user.role !== "superadmin" && user.role !== "investor") {
      setViewRole("");
      try { localStorage.removeItem("nia_view_role"); } catch {}
      return;
    }
    if (user.role === "investor" && !viewRole) {
      setViewRole("admin");
      try { localStorage.setItem("nia_view_role", "admin"); } catch {}
    }
  }, [user?.role, viewRole]);

  const setViewRoleSafe = useCallback((nextRole) => {
    if (!user?.role || (user.role !== "superadmin" && user.role !== "investor")) return;
    const value = nextRole || "";
    setViewRole(value);
    try {
      if (value) localStorage.setItem("nia_view_role", value);
      else localStorage.removeItem("nia_view_role");
    } catch {}
  }, [user?.role]);

  const effectiveRole =
    user?.role === "superadmin" || user?.role === "investor"
      ? (viewRole || user?.role)
      : user?.role;

  const isReadOnly = user?.role === "investor";

  const value = useMemo(
    () => ({
      user,
      effectiveRole,
      isReadOnly,
      viewRole,
      setViewRole: setViewRoleSafe,
      loading,
      login,
      signup,
      loginWithGoogle,
      logout,
      updateRole,
      updateProfile,
      updateDashboardActionState,
      orgs,
      orgsLoading,
      orgLoadStatus,
      orgLoadError,
      orgSelectionError,
      activeOrgId: resolvedActiveOrgId,
      orgAccessReady,
      setActiveOrgId,
      firestoreError,
      clearFirestoreError: () => setFirestoreError(""),
    }),
    [user, effectiveRole, isReadOnly, viewRole, setViewRoleSafe, loading, logout, updateRole, updateProfile, updateDashboardActionState, orgs, orgsLoading, orgLoadStatus, orgLoadError, orgSelectionError, resolvedActiveOrgId, orgAccessReady, setActiveOrgId, firestoreError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
